'use client';

import React, { useEffect, useState, useRef } from 'react';

interface DictionaryResult {
  word: string;
  phonetic?: string;
  meanings: {
    partOfSpeech: string;
    definitions: {
      definition: string;
      example?: string;
    }[];
  }[];
}

export default function SmartDictionary() {
  const [selectedWord, setSelectedWord] = useState<string>('');
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [result, setResult] = useState<DictionaryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check initial screen size and listen for resizes
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile(); // initial check
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        // Don't close immediately if clicking inside the tooltip
        return;
      }

      const text = selection.toString().trim();
      // Allow single words AND short phrases (up to 5 words, max 60 chars)
      // Must only contain letters, spaces, hyphens, apostrophes
      const wordCount = text.split(/\s+/).filter(Boolean).length;
      if (!text || wordCount > 5 || text.length > 60 || !/^[a-zA-Z\s'\-]+$/.test(text)) {
        return;
      }

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Set position just above the selected text
      setPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
      setSelectedWord(text);
    };

    const handleDocumentClick = (e: MouseEvent | TouchEvent) => {
      // If clicking inside the tooltip, do nothing
      if (tooltipRef.current && tooltipRef.current.contains(e.target as Node)) {
        return;
      }
      
      // Check if there's a valid selection
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
         setPosition(null);
         setResult(null);
         setError(false);
      }
    };

    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('touchend', handleSelection);
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('touchstart', handleDocumentClick);

    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('touchend', handleSelection);
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
    };
  }, []);

  // Fetch dictionary data when word changes
  useEffect(() => {
    if (!selectedWord) return;

    let isMounted = true;
    const fetchMeaning = async () => {
      setLoading(true);
      setError(false);
      setResult(null);
      
      // For phrases, use the last significant word for dictionary lookup
      // (e.g. "artificial intelligence" → try "intelligence")
      const words = selectedWord.trim().split(/\s+/).filter(Boolean);
      const lookupWord = words.length > 1
        ? words[words.length - 1]  // last word of phrase
        : words[0];

      try {
        const res = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${lookupWord.toLowerCase()}`);
        if (!res.ok) {
          throw new Error('Not found');
        }
        const data = await res.json();
        if (isMounted && data && data.length > 0) {
          // If it was a phrase, show full phrase as title but use fetched definition
          const resultData = { ...data[0], word: selectedWord };
          setResult(resultData);
        }
      } catch (err) {
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Small delay to avoid API spam on drag-select
    const timeoutId = setTimeout(fetchMeaning, 400);
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [selectedWord]);

  if (!position) return null;

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobile && (
        <div className="fixed inset-0 bg-black/40 z-[9998] backdrop-blur-sm pointer-events-none" />
      )}
      
      <div
        ref={tooltipRef}
        className={`fixed z-[9999] flex flex-col items-center pointer-events-auto transition-all ${
          isMobile 
            ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' 
            : '-translate-x-1/2 -translate-y-full'
        }`}
        style={isMobile ? {} : { left: position.x, top: position.y }}
      >
        <div className={`bg-foreground text-background w-[300px] sm:w-[320px] rounded-xl shadow-2xl p-4 sm:p-5 text-left border border-border/10 ${isMobile ? 'shadow-[0_0_40px_rgba(0,0,0,0.3)]' : ''}`}>
          {loading && (
            <div className="flex items-center gap-3 text-background/70">
              <div className="w-4 h-4 border-2 border-background/20 border-t-background rounded-full animate-spin" />
              <span className="text-xs font-bold uppercase tracking-widest">Searching...</span>
            </div>
          )}

          {error && !loading && (
            <div className="text-sm text-background/80">
              <span className="font-bold">{selectedWord}</span> — No definition found.
            </div>
          )}

          {result && !loading && (
            <div>
              <div className="flex items-baseline gap-2 mb-1">
                <h4 className="font-serif text-xl sm:text-2xl font-bold capitalize text-background">{result.word}</h4>
                {result.phonetic && <span className="text-xs font-mono text-brand-light opacity-80">{result.phonetic}</span>}
              </div>
              
              <div className="max-h-[200px] sm:max-h-[150px] overflow-y-auto pr-2 custom-scrollbar">
                {result.meanings.slice(0, 2).map((meaning, idx) => (
                  <div key={idx} className="mt-3">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-background/50 border border-background/20 rounded-sm px-1 py-0.5">
                      {meaning.partOfSpeech}
                    </span>
                    <p className="text-sm leading-snug mt-1.5 text-background/90">
                      {meaning.definitions[0].definition}
                    </p>
                    {meaning.definitions[0].example && (
                      <p className="text-xs italic text-background/60 mt-1">
                        "{meaning.definitions[0].example}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-background/10 flex justify-between items-center">
                  <span className="text-[9px] uppercase tracking-widest text-background/40 font-bold">WorldPrime Smart Dictionary</span>
                  <a href={`https://www.google.com/search?q=define+${selectedWord}`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-bold text-brand-light hover:underline">
                      More
                  </a>
              </div>
            </div>
          )}
        </div>
        {!isMobile && <div className="w-4 h-4 bg-foreground rotate-45 -mt-2 -mb-2 z-[-1]" />}
      </div>
    </>
  );
}
